import { useQueryClient, useMutation } from '@tanstack/react-query';
import { toBillableMaterial } from '../../../components/BillableMaterials/billableMaterial.helper';
import { CreateBillableMaterial, BillableMaterial } from '../../../components/BillableMaterials/types';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { BillableMaterialsService } from '../../generated';
import { billableMaterialsKeys } from '../../queries/billableMaterials';

interface CreateBillableMaterialProps {
  organisationId: string;
  data: CreateBillableMaterial;
}

export function useCreateBillableMaterial() {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organisationId, data }: CreateBillableMaterialProps) => {
      const newMaterial = {
        name: data.name,
        article_number: data.articleNumber,
        jobsite_ids: data.jobsiteIds,
        condition_set: data.conditionSets,
      };

      const response = await BillableMaterialsService.billableMaterialControllerCreateBillableMaterial({
        path: {
          organisationId,
        },
        body: newMaterial,
      });

      if (response.data !== undefined) {
        return toBillableMaterial(response.data);
      } else {
        throw (
          (response as any).error ||
          new Error(response?.response?.statusText || 'Unknown error when creating billable material')
        );
      }
    },
    onSuccess: (newMaterial: BillableMaterial, variables: CreateBillableMaterialProps) => {
      if (newMaterial) {
        queryClient.setQueryData<BillableMaterial[]>(
          billableMaterialsKeys.read({ organisationId: variables.organisationId }),
          (old) => (old ? [...old, newMaterial] : [newMaterial])
        );
      }
      showSnackbar('billableMaterial.createSuccess', 'success');
    },
    onError: async () => {
      showSnackbar('billableMaterial.createError', 'error');
    },
  });
}
